import { useCallback, useEffect, useMemo, useState } from "react";

import { api, type LineLinkCodeData } from "../lib/api";
import { LINE_OFFICIAL_ACCOUNT_URL } from "../lib/config";
import { AppError, logError, toUserMessage } from "../lib/errors";
import {
  getLineLinkStatusDescription,
  getLineLinkStatusLabel,
  markLineLinkPending,
  readLineLinkSnapshot,
  resolveLineLinkSnapshotFromApiStatus,
  type LineLinkSnapshot,
  writeLineLinkSnapshot,
} from "../lib/lineLink";
import { UI_TEXT_TONE } from "../lib/uiTone";

function formatExpiresAt(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("ja-JP", {
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function formatStatusCheckedAt(value?: string) {
  if (!value) return "未確認";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "未確認";
  }

  return new Intl.DateTimeFormat("ja-JP", {
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(date);
}

function getPendingMessage(linkCode?: string) {
  return linkCode ? `連携 ${linkCode}` : "";
}

function getLineLinkErrorMessage(error: unknown) {
  if (!(error instanceof AppError)) {
    return toUserMessage(error);
  }

  if (error.code === "NOT_FOUND") {
    return "LINE連携の準備がまだ反映されていません。しばらくしてからもう一度お試しください。";
  }

  if (error.code === "UNAUTHORIZED") {
    return "認証の有効期限が切れている可能性があります。時間をおいてもう一度お試しください。";
  }

  if (error.code === "MAINTENANCE") {
    return "現在メンテナンス中のため、LINE連携コードを発行できません。しばらくしてから再度お試しください。";
  }

  return toUserMessage(error);
}

function getLineLinkDisconnectErrorMessage(error: unknown) {
  if (!(error instanceof AppError)) {
    return toUserMessage(error);
  }

  if (error.code === "NOT_FOUND") {
    return "LINE連携解除の準備がまだ反映されていません。しばらくしてからもう一度お試しください。";
  }

  if (error.code === "UNAUTHORIZED") {
    return "認証の有効期限が切れている可能性があります。時間をおいてもう一度お試しください。";
  }

  if (error.code === "MAINTENANCE") {
    return "現在メンテナンス中のため、LINE連携を解除できません。しばらくしてから再度お試しください。";
  }

  return toUserMessage(error);
}

export function LineLinkCard() {
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [disconnectSubmitting, setDisconnectSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);
  const [disconnectSuccess, setDisconnectSuccess] = useState<string | null>(null);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const [linkData, setLinkData] = useState<LineLinkCodeData | null>(null);
  const [disconnectConfirmOpen, setDisconnectConfirmOpen] = useState(false);
  const [lineLinkSnapshot, setLineLinkSnapshot] = useState<LineLinkSnapshot>(readLineLinkSnapshot());

  const pendingMessage = useMemo(() => getPendingMessage(linkData?.link_code), [linkData?.link_code]);
  const statusLabel = useMemo(() => getLineLinkStatusLabel(lineLinkSnapshot), [lineLinkSnapshot]);
  const statusDescription = useMemo(
    () => getLineLinkStatusDescription(lineLinkSnapshot),
    [lineLinkSnapshot]
  );
  const statusCheckedAt = useMemo(
    () => formatStatusCheckedAt(lineLinkSnapshot.updatedAt),
    [lineLinkSnapshot.updatedAt]
  );
  const shouldShowStatusPanel = useMemo(
    () => lineLinkSnapshot.status !== "unlinked" || !!linkData,
    [lineLinkSnapshot.status, linkData]
  );
  const guidanceLines = useMemo(() => {
    if (lineLinkSnapshot.status === "linked") {
      return [
        "現在はLINE連携済みです。ニュースを送ってもらう画面で、LINEを通知先として選べます。",
        "LINEを選んで依頼すると、ニュースをLINEで受け取れます。",
        "解除すると、LINEを通知先としては使えなくなります。",
      ];
    }

    if (lineLinkSnapshot.status === "pending") {
      return [
        "表示されたメッセージを、LINE公式アカウントにそのまま送信してください。",
        "送信後は状態を自動で確認します。反映まで少し時間がかかる場合があります。",
        "連携が完了すると、ニュースを送ってもらう画面でLINEを通知先に選べます。",
      ];
    }

    if (lineLinkSnapshot.status === "error") {
      return [
        "もう一度コードを発行し、LINE公式アカウントにメッセージを送信してください。",
        "連携が完了しない場合は、少し時間をおいてからやり直してください。",
        "LINE連携が完了するまでは、ニュースの通知先にLINEは選べません。",
      ];
    }

    return [
      "LINEでニュースを受け取りたい場合は、「LINE連携開始」を押して6桁の連携コードを発行します。",
      "LINE公式アカウントに表示されたメッセージを送信すると、連携が完了します。",
      "連携後は、ニュースを送ってもらう画面でLINEを通知先に選べます。",
    ];
  }, [lineLinkSnapshot.status]);

  const loadLineLinkStatus = useCallback(async () => {
    setStatusLoading(true);
    setStatusError(null);

    try {
      const response = await api.getLineLinkStatus();
      const nextSnapshot = resolveLineLinkSnapshotFromApiStatus(response, readLineLinkSnapshot());
      setLineLinkSnapshot(nextSnapshot);
      writeLineLinkSnapshot(nextSnapshot);
      if (nextSnapshot.status === "linked") {
        setLinkData(null);
        setCopyMessage(null);
      }
    } catch (e) {
      logError(e);
      setStatusError("連携状態を確認できませんでした。");
      setLineLinkSnapshot(readLineLinkSnapshot());
    } finally {
      setStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadLineLinkStatus();
  }, [loadLineLinkStatus]);

  useEffect(() => {
    if (lineLinkSnapshot.status !== "pending") return;

    const intervalId = window.setInterval(() => {
      if (statusLoading || loading || disconnectSubmitting) return;
      void loadLineLinkStatus();
    }, 3000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [disconnectSubmitting, lineLinkSnapshot.status, loadLineLinkStatus, loading, statusLoading]);

  useEffect(() => {
    const refreshOnReturn = () => {
      if (loading || disconnectSubmitting) return;
      void loadLineLinkStatus();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      refreshOnReturn();
    };

    window.addEventListener("focus", refreshOnReturn);
    window.addEventListener("pageshow", refreshOnReturn);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", refreshOnReturn);
      window.removeEventListener("pageshow", refreshOnReturn);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [disconnectSubmitting, loadLineLinkStatus, loading]);

  const openOfficialAccount = () => {
    if (!LINE_OFFICIAL_ACCOUNT_URL) return;
    window.open(LINE_OFFICIAL_ACCOUNT_URL, "_blank", "noopener,noreferrer");
  };

  const handleCreateCode = async () => {
    setLoading(true);
    setError(null);
    setDisconnectError(null);
    setDisconnectSuccess(null);
    setCopyMessage(null);

    try {
      const nextLinkData = await api.createLineLinkCode();
      setLinkData(nextLinkData);
      markLineLinkPending();
      const nextSnapshot = {
        ...readLineLinkSnapshot(),
        status: "pending" as const,
        updatedAt: new Date().toISOString(),
      };
      setLineLinkSnapshot(nextSnapshot);
      writeLineLinkSnapshot(nextSnapshot);
    } catch (e) {
      logError(e);
      setError(getLineLinkErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!pendingMessage || !navigator.clipboard) {
      setCopyMessage("コピーに失敗しました。");
      return;
    }

    try {
      await navigator.clipboard.writeText(pendingMessage);
      setCopyMessage("コピーしました。");
    } catch (e) {
      logError(e);
      setCopyMessage("コピーに失敗しました。");
    }
  };

  const handleOpenDisconnectConfirm = () => {
    if (loading || statusLoading || disconnectSubmitting) return;

    setError(null);
    setDisconnectError(null);
    setDisconnectSuccess(null);
    setDisconnectConfirmOpen(true);
  };

  const handleCloseDisconnectConfirm = () => {
    if (disconnectSubmitting) return;
    setDisconnectConfirmOpen(false);
  };

  const handleDisconnectLineLink = async () => {
    setDisconnectSubmitting(true);
    setError(null);
    setDisconnectError(null);
    setDisconnectSuccess(null);

    try {
      const response = await api.disconnectLineLink();

      if (!response.disconnected) {
        throw new AppError("Line link disconnect failed.", "INVALID_RESPONSE");
      }

      const nextSnapshot: LineLinkSnapshot = {
        status: "unlinked",
        updatedAt: new Date().toISOString(),
      };

      setLineLinkSnapshot(nextSnapshot);
      writeLineLinkSnapshot(nextSnapshot);
      setLinkData(null);
      setCopyMessage(null);
      setDisconnectSuccess("LINE連携を解除しました。再度利用する場合は、あらためてLINE連携を行ってください。");
      setDisconnectConfirmOpen(false);
      await loadLineLinkStatus();
    } catch (e) {
      logError(e);
      setDisconnectError(getLineLinkDisconnectErrorMessage(e));
    } finally {
      setDisconnectSubmitting(false);
    }
  };

  return (
    <>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <p className={`max-w-md text-sm leading-6 ${UI_TEXT_TONE.body}`}>
          ニュース通知の受け取りに向けて、WizMateアカウントとLINE公式アカウントを連携します。
        </p>

        <div className="flex flex-col gap-2 sm:items-end">
          {lineLinkSnapshot.status !== "linked" ? (
            <button
              type="button"
              onClick={handleCreateCode}
              disabled={loading || disconnectSubmitting}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-full bg-[#5F8B57] px-5 py-3 text-sm font-black text-white transition hover:bg-[#4E7348] disabled:cursor-not-allowed disabled:opacity-70"
            >
              {loading ? "発行中..." : linkData ? "コードを再発行" : "LINE連携開始"}
            </button>
          ) : null}

          {lineLinkSnapshot.status === "linked" ? (
            <button
              type="button"
              onClick={handleOpenDisconnectConfirm}
              disabled={loading || statusLoading || disconnectSubmitting}
              className="inline-flex items-center justify-center rounded-full border border-[#C8D8C2] bg-white px-4 py-2 text-xs font-bold text-[#4A604B] transition hover:bg-[#F8FCF5] disabled:cursor-not-allowed disabled:opacity-70"
            >
              {disconnectSubmitting ? "解除中..." : "連携を解除"}
            </button>
          ) : null}
        </div>
      </div>

      {shouldShowStatusPanel ? (
        <div className="mt-4 rounded-[1.8rem] border border-[#D8E7D3] bg-[#F7FBF3] px-5 py-5 shadow-[0_10px_24px_rgba(95,139,87,0.08)]">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className={`text-[12px] font-bold tracking-[0.14em] ${UI_TEXT_TONE.eyebrow}`}>現在の連携状況</p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="rounded-full border border-[#C8D8C2] bg-white px-3 py-1 text-xs font-black text-[#4A604B]">
                  {statusLoading ? "確認中..." : statusLabel}
                </span>
                {lineLinkSnapshot.status === "pending" ? (
                  <span className="text-xs font-bold text-[#6F8B63]">
                    LINEで操作後にこの画面へ戻ると、状態を自動で確認します。
                  </span>
                ) : null}
              </div>
              <p className={`mt-3 text-sm leading-6 ${UI_TEXT_TONE.sectionTitle}`}>{statusDescription}</p>
              <p className={`mt-2 text-xs ${UI_TEXT_TONE.meta}`}>最終確認: {statusCheckedAt}</p>
            </div>

            <button
              type="button"
              onClick={() => void loadLineLinkStatus()}
              disabled={statusLoading || loading || disconnectSubmitting}
              className="inline-flex items-center justify-center rounded-full border border-[#C8D8C2] bg-white px-4 py-2.5 text-sm font-bold text-[#4A604B] transition hover:bg-[#F8FCF5] disabled:cursor-not-allowed disabled:opacity-70"
            >
              {statusLoading ? "確認中..." : "状態を更新"}
            </button>
          </div>
        </div>
      ) : null}

      <div className={`mt-4 rounded-[1.5rem] border border-white/70 bg-white/80 p-4 text-sm leading-6 ${UI_TEXT_TONE.sectionTitle}`}>
        {guidanceLines.map((line, index) => (
          <p key={line}>
            {index + 1}. {line}
          </p>
        ))}
      </div>

      {statusError ? (
        <div className="mt-4 rounded-[1.4rem] border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-3 text-sm text-[#A1493E]">
          {statusError}
        </div>
      ) : null}

      {disconnectSuccess ? (
        <div className="mt-4 rounded-[1.4rem] border border-[#D8E7D3] bg-[#F7FBF3] px-4 py-3 text-sm text-[#58705A]">
          {disconnectSuccess}
        </div>
      ) : null}

      {disconnectError ? (
        <div className="mt-4 rounded-[1.4rem] border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-3 text-sm text-[#A1493E]">
          {disconnectError}
        </div>
      ) : null}

      {error && (
        <div className="mt-4 rounded-[1.4rem] border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-3 text-sm text-[#A1493E]">
          {error}
        </div>
      )}

      {linkData && (
        <div className="mt-4 rounded-[1.8rem] border border-[#D5E3D1] bg-white px-5 py-5 shadow-[0_10px_24px_rgba(95,139,87,0.08)]">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className={`text-[12px] font-bold tracking-[0.14em] ${UI_TEXT_TONE.eyebrow}`}>連携コード</p>
              <p className={`mt-2 font-mono text-3xl font-black tracking-[0.35em] ${UI_TEXT_TONE.sectionTitle}`}>
                {linkData.link_code}
              </p>
            </div>

            <div className="rounded-2xl bg-[#F2F7EE] px-4 py-3 text-right">
              <p className={`text-[11px] font-bold tracking-[0.12em] ${UI_TEXT_TONE.eyebrow}`}>有効期限</p>
              <p className={`mt-1 text-sm font-bold ${UI_TEXT_TONE.sectionTitle}`}>{formatExpiresAt(linkData.expires_at)} まで</p>
            </div>
          </div>

          <div className="mt-4 rounded-[1.4rem] border border-dashed border-[#C8D8C2] bg-[#F8FCF5] px-4 py-4">
            <p className={`text-xs font-bold uppercase tracking-[0.16em] ${UI_TEXT_TONE.eyebrow}`}>LINEに送るメッセージ</p>
            <p className={`mt-2 whitespace-pre-line text-base font-black leading-7 ${UI_TEXT_TONE.sectionTitle}`}>
              {linkData.instruction}
            </p>
            <p className={`mt-2 rounded-xl bg-white px-3 py-2 font-mono text-sm font-bold ${UI_TEXT_TONE.sectionTitle}`}>
              {pendingMessage}
            </p>
          </div>

          <div className="mt-4 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={handleCopy}
                className="inline-flex items-center justify-center rounded-full border border-[#C8D8C2] bg-white px-4 py-2.5 text-sm font-bold text-[#6A4F44] transition hover:bg-[#F8FCF5]"
              >
                メッセージをコピー
              </button>

            {LINE_OFFICIAL_ACCOUNT_URL && (
              <button
                type="button"
                onClick={openOfficialAccount}
                className="inline-flex items-center justify-center rounded-full bg-[#06C755] px-4 py-2.5 text-sm font-black text-white transition hover:bg-[#05AE4A]"
              >
                LINE公式アカウントを開く
              </button>
            )}
          </div>

          {copyMessage && <p className="mt-3 text-xs font-bold text-[#5F8B57]">{copyMessage}</p>}
          {!LINE_OFFICIAL_ACCOUNT_URL && (
            <p className={`mt-3 text-xs ${UI_TEXT_TONE.meta}`}>
              LINE公式アカウントのURLは未設定です。友だち追加済みの公式アカウントへ「連携 123456」の形式で送信してください。
            </p>
          )}
        </div>
      )}

      {disconnectConfirmOpen ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center overflow-x-hidden bg-[#2B211D]/45 px-4 sm:px-6">
          <div className="w-full max-w-sm rounded-[2rem] border border-[#D8E7D3] bg-white p-6 shadow-[0_24px_60px_rgba(62,85,64,0.18)]">
            <p className={`text-[12px] font-black uppercase tracking-[0.18em] ${UI_TEXT_TONE.eyebrow}`}>確認</p>
            <h3 className={`mt-2 text-xl font-black ${UI_TEXT_TONE.sectionTitle}`}>LINE連携を解除しますか？</h3>
            <p className={`mt-3 text-sm leading-6 ${UI_TEXT_TONE.bodyMuted}`}>
              解除すると、WizMateからのニュース通知をLINEで受け取れなくなります。再度利用するには、あらためてLINE連携が必要です。
            </p>

            <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row">
              <button
                type="button"
                onClick={handleCloseDisconnectConfirm}
                disabled={disconnectSubmitting}
                className="flex-1 rounded-full border border-[#C8D8C2] bg-white px-4 py-3 text-sm font-bold text-[#6A4F44] transition hover:bg-[#F8FCF5] disabled:cursor-not-allowed disabled:opacity-70"
              >
                キャンセル
              </button>
              <button
                type="button"
                onClick={handleDisconnectLineLink}
                disabled={disconnectSubmitting}
                className="flex-1 rounded-full bg-[#A1493E] px-4 py-3 text-sm font-black text-white transition hover:bg-[#8E3E35] disabled:cursor-not-allowed disabled:opacity-70"
              >
                {disconnectSubmitting ? "解除中..." : "解除する"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
